import React from 'react';
import { Box, Container, Typography, Grid, Stack, Chip } from '@mui/material';
import {
  Description, AccountBalance, CameraAlt, Gavel,
  TrendingUp, Chat, CurrencyExchange, SmartToy,
  Construction, Search, AttachMoney, Assessment,
  GpsFixed, Verified, BarChart,
} from '@mui/icons-material';
import { brand, text, bg, shadow } from '../../theme/colors';

/* ── Section heading helper ────────────────────────────────────────────── */
const SectionTitle = ({ children, sub }) => (
  <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
    <Typography variant="h4" sx={{ fontWeight: 800, color: text.primary, mb: sub ? 1.5 : 0 }}>
      {children}
    </Typography>
    {sub && (
      <Typography sx={{ color: text.secondary, fontSize: '1.05rem', lineHeight: 1.7, maxWidth: 600, mx: 'auto' }}>
        {sub}
      </Typography>
    )}
  </Box>
);

/* ── Feature card for role sections ────────────────────────────────────── */
const FeatureCard = ({ icon, title, desc }) => (
  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
    <Box
      sx={{
        flexShrink: 0,
        width: 40, height: 40,
        borderRadius: 1.5,
        bgcolor: 'rgba(13, 148, 136, 0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: brand.primary,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: text.primary, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography sx={{ color: text.secondary, fontSize: '0.9rem', lineHeight: 1.6 }}>
        {desc}
      </Typography>
    </Box>
  </Box>
);

/* ── Comparison row for "The Problem" section ─────────────────────────── */
const ComparisonRow = ({ problem, solution }) => (
  <Box sx={{ mb: 3 }}>
    <Typography sx={{ color: text.secondary, fontSize: '0.95rem', lineHeight: 1.7, mb: 0.5, fontStyle: 'italic' }}>
      {problem}
    </Typography>
    <Typography sx={{ color: text.primary, fontSize: '0.95rem', lineHeight: 1.7, fontWeight: 600 }}>
      {solution}
    </Typography>
  </Box>
);

/* ── Data ──────────────────────────────────────────────────────────────── */
const problems = [
  {
    problem: 'You send money to a relative or custodian — and prices get inflated, funds disappear.',
    solution: 'Eqaya holds your money in a secure wallet. It only releases when YOU approve the work.',
  },
  {
    problem: 'You pay a contractor a deposit and they disappear. Or promise Monday and show up Friday.',
    solution: 'Eqaya uses milestone-based payments. No work, no pay. Contractors earn as they deliver.',
  },
  {
    problem: 'Photos get staged. You can\'t tell if the foundation is 6 inches or 6 feet deep.',
    solution: 'Eqaya sends independent inspectors to your site with GPS-locked, timestamped evidence. No desk reports. No favours.',
  },
  {
    problem: 'Shoddy work with substandard materials — and by the time you find out, the money is gone.',
    solution: 'Eqaya\'s legally binding digital contracts lock in scope, materials, and cost upfront. AI checks for building code compliance.',
  },
];

const clientFeatures = [
  { icon: <Description fontSize="small" />, title: 'Post Your Project', desc: 'Describe what you need — AI helps you write a professional brief in minutes.' },
  { icon: <Search fontSize="small" />, title: 'Compare Proposals', desc: 'Receive bids from contractors. AI analyses pricing, scope, and flags the best value.' },
  { icon: <Gavel fontSize="small" />, title: 'Legally Binding Contracts', desc: 'Digitally signed, enforceable, with full audit trail. No handshake deals.' },
  { icon: <AccountBalance fontSize="small" />, title: 'Secure Wallet', desc: 'Fund milestones upfront. Money releases only when you approve — or auto-releases after 72 hours.' },
  { icon: <CameraAlt fontSize="small" />, title: 'Independent Inspections', desc: 'GPS-locked site visits with timestamped photos. You see what\'s really happening.' },
  { icon: <Chat fontSize="small" />, title: 'Direct Messaging', desc: 'Talk to your contractor in real time. Get daily photo updates and progress reports.' },
];

const contractorFeatures = [
  { icon: <Construction fontSize="small" />, title: 'Find Projects', desc: 'Browse a marketplace of real projects. Get matched by AI based on your skills and location.' },
  { icon: <AttachMoney fontSize="small" />, title: 'Fair Milestone Payments', desc: 'Get paid as you deliver — not months later. 72-hour auto-release means no chasing clients.' },
  { icon: <TrendingUp fontSize="small" />, title: 'Build Your Reputation', desc: 'Every completed project adds to your portfolio with verified reviews and ratings.' },
  { icon: <CurrencyExchange fontSize="small" />, title: 'EcoCash or Bank Transfer', desc: 'Withdraw earnings via mobile money or bank. Multi-currency support (USD, GBP, EUR, ZAR).' },
];

const inspectorFeatures = [
  { icon: <GpsFixed fontSize="small" />, title: 'GPS-Verified Inspections', desc: 'Geofenced to within 100m of the site. Your evidence is tamper-proof and location-stamped.' },
  { icon: <SmartToy fontSize="small" />, title: 'AI-Powered Reports', desc: 'AI helps generate professional reports with defect detection and compliance checks.' },
  { icon: <Verified fontSize="small" />, title: 'Earn Per Inspection', desc: 'Accept jobs that fit your schedule. Build a public portfolio of completed verifications.' },
  { icon: <BarChart fontSize="small" />, title: 'Full Dashboard', desc: 'Track assignments, earnings, ratings, and verification accuracy — all in one place.' },
];

const moneyBreakdown = [
  { pct: '97.5%', label: 'Contractors', desc: 'Direct payment for completed milestone work.', color: brand.primary },
  { pct: '2.5%', label: 'Platform', desc: 'Keeping Eqaya running, growing, and reaching more families.', color: brand.accent },
];

const stats = [
  { value: '$10M+', label: 'Protected' },
  { value: '500+', label: 'Families served' },
  { value: '150+', label: 'Inspectors' },
  { value: '99.2%', label: 'Inspection accuracy' },
  { value: '10', label: 'Project categories' },
];

const audiences = [
  'Diaspora families building in Zimbabwe',
  'Property owners wanting accountability',
  'Contractors seeking fair payment',
  'Inspectors earning from quality work',
  'Anyone burned by broken promises',
  'First-time builders needing guidance',
  'Renovation & repair projects',
  'Solar, borehole & electrical work',
];

/* ── Component ─────────────────────────────────────────────────────────── */
export default function WelcomeExplainer() {
  return (
    <Box>
      {/* ── Welcome + What is Eqaya ─────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: bg.earth }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography
            sx={{ color: brand.primary, fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase', mb: 2 }}
          >
            Welcome
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, color: text.primary, mb: 3 }}>
            Welcome to Eqaya!
          </Typography>

          <Typography sx={{ color: text.secondary, fontSize: '1.1rem', lineHeight: 1.8, maxWidth: 700, mx: 'auto', mb: 4 }}>
            Your construction marketplace to find contractors, protect your investment, and build in Zimbabwe with confidence — from anywhere in the world.
          </Typography>

          {/* Founder quote */}
          <Box
            sx={{
              bgcolor: bg.paper,
              borderRadius: 3,
              p: { xs: 3, md: 4 },
              maxWidth: 640,
              mx: 'auto',
              borderLeft: `4px solid ${brand.accent}`,
              boxShadow: shadow.sm,
            }}
          >
            <Typography
              sx={{ color: text.primary, fontSize: '1rem', lineHeight: 1.8, fontStyle: 'italic' }}
            >
              "We built Eqaya because we lived the problem. Sending money home for construction and hoping for the best shouldn't be the only option. Every project deserves a contract, every milestone deserves proof, and every family deserves peace of mind."
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── The Problem ─────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: bg.paper }}>
        <Container maxWidth="md">
          <SectionTitle sub="Building from abroad is broken. Eqaya fixes it.">
            The Problem Eqaya Solves
          </SectionTitle>

          {problems.map((p, i) => (
            <ComparisonRow key={i} problem={p.problem} solution={p.solution} />
          ))}
        </Container>
      </Box>

      {/* ── Key Features by Role ────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: bg.earth }}>
        <Container maxWidth="lg">
          <SectionTitle sub="Three roles, one platform, one mission.">
            Key Features &amp; Benefits
          </SectionTitle>

          <Grid container spacing={4}>
            {/* For Project Owners */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  bgcolor: bg.paper,
                  borderRadius: 3,
                  p: { xs: 3, md: 3.5 },
                  height: '100%',
                  boxShadow: shadow.sm,
                  borderTop: `3px solid ${brand.primary}`,
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: brand.primary, mb: 0.5 }}>
                  For Project Owners
                </Typography>
                <Typography sx={{ color: text.secondary, fontSize: '0.85rem', mb: 3 }}>
                  Build from abroad with full control
                </Typography>
                <Stack spacing={3}>
                  {clientFeatures.map((f) => (
                    <FeatureCard key={f.title} {...f} />
                  ))}
                </Stack>
              </Box>
            </Grid>

            {/* For Contractors */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  bgcolor: bg.paper,
                  borderRadius: 3,
                  p: { xs: 3, md: 3.5 },
                  height: '100%',
                  boxShadow: shadow.sm,
                  borderTop: `3px solid ${brand.secondary}`,
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: brand.secondary, mb: 0.5 }}>
                  For Contractors
                </Typography>
                <Typography sx={{ color: text.secondary, fontSize: '0.85rem', mb: 3 }}>
                  Get projects, get paid, build your name
                </Typography>
                <Stack spacing={3}>
                  {contractorFeatures.map((f) => (
                    <FeatureCard key={f.title} {...f} />
                  ))}
                </Stack>
              </Box>
            </Grid>

            {/* For Inspectors */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  bgcolor: bg.paper,
                  borderRadius: 3,
                  p: { xs: 3, md: 3.5 },
                  height: '100%',
                  boxShadow: shadow.sm,
                  borderTop: `3px solid ${brand.accent}`,
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: brand.accent, mb: 0.5 }}>
                  For Inspectors
                </Typography>
                <Typography sx={{ color: text.secondary, fontSize: '0.85rem', mb: 3 }}>
                  Earn by providing honest quality reports
                </Typography>
                <Stack spacing={3}>
                  {inspectorFeatures.map((f) => (
                    <FeatureCard key={f.title} {...f} />
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Where Your Money Goes ───────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: bg.paper }}>
        <Container maxWidth="sm">
          <SectionTitle sub="Full transparency. Every dollar tracked. No hidden fees.">
            Where Your Money Goes
          </SectionTitle>

          <Stack spacing={3}>
            {moneyBreakdown.map((m) => (
              <Box
                key={m.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  p: 3,
                  bgcolor: bg.earth,
                  borderRadius: 2.5,
                  borderLeft: `4px solid ${m.color}`,
                }}
              >
                <Typography sx={{ fontWeight: 900, fontSize: '2rem', color: m.color, minWidth: 80, textAlign: 'center' }}>
                  {m.pct}
                </Typography>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: text.primary }}>
                    {m.label}
                  </Typography>
                  <Typography sx={{ color: text.secondary, fontSize: '0.9rem', lineHeight: 1.5 }}>
                    {m.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ── Results ─────────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: brand.primaryDeep }}>
        <Container maxWidth="md">
          <SectionTitle sub="Real impact, not vanity metrics.">
            <Box component="span" sx={{ color: 'white' }}>Results?</Box>
          </SectionTitle>

          <Grid container spacing={3} justifyContent="center">
            {stats.map((s) => (
              <Grid item xs={6} sm={4} md key={s.label}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: brand.accentLight }}>
                    {s.value}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 500 }}>
                    {s.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Who is Eqaya For ────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: bg.earth }}>
        <Container maxWidth="md">
          <SectionTitle sub="Whether you want to build, work, or inspect — Eqaya is your platform.">
            Who is Eqaya For?
          </SectionTitle>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
            {audiences.map((a) => (
              <Chip
                key={a}
                label={a}
                sx={{
                  bgcolor: bg.paper,
                  color: text.primary,
                  fontWeight: 600,
                  fontSize: '0.88rem',
                  py: 2.5,
                  px: 1,
                  borderRadius: 2,
                  boxShadow: shadow.xs,
                  border: `1px solid ${bg.hover}`,
                }}
              />
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
